// Step.tsx
import { cva, VariantProps } from 'class-variance-authority';
import { CheckIcon } from 'lucide-react';
import React from 'react';

import { cn } from '@/shared/utils/cn';

/* ────────────────────────────────────────────────────────────────────────── */
/* 1. Variants                                                               */
/* ────────────────────────────────────────────────────────────────────────── */

const stepVariants = cva('text-base font-semibold', {
  variants: {
    orientation: {
      vertical: 'flex flex-row gap-2',
      horizontal: 'relative flex flex-col items-center flex-1',
    },
    progress: {
      prev: 'text-white',
      current: 'text-white/50',
      next: 'text-white/50',
    },
  },
  defaultVariants: {
    orientation: 'vertical',
    progress: 'next',
  },
});

/* ────────────────────────────────────────────────────────────────────────── */
/* 2. Props                                                                  */
/* ────────────────────────────────────────────────────────────────────────── */

export type StepProps = React.HTMLAttributes<HTMLLIElement> &
  VariantProps<typeof stepVariants> & {
    index: number; // 1-based
    currentStep: number; // 1-based
    totalSteps: number; // 1-based
  };

/* ────────────────────────────────────────────────────────────────────────── */
/* 3. Component                                                              */
/* ────────────────────────────────────────────────────────────────────────── */

export const Step = ({
  index,
  currentStep,
  totalSteps,
  orientation = 'vertical',
  children,
  ...props
}: StepProps) => {
  const progress: 'prev' | 'current' | 'next' =
    currentStep === index ? 'current' : currentStep > index ? 'prev' : 'next';

  const isLast = index === totalSteps;
  const showLine = !isLast;

  /* dot colours (identical to original vertical version) */
  const dotBg =
    progress === 'prev' ? 'bg-white text-black' : 'bg-[rgb(45,47,73)]';

  /* ---------------------------------------------------------------------- */
  /* VERTICAL RENDER                                                        */
  /* ---------------------------------------------------------------------- */
  if (orientation === 'vertical') {
    return (
      <li
        {...props}
        className={cn(stepVariants({ orientation, progress }), props.className)}
      >
        <div>
          {/* dot + label */}
          <div className="flex items-center gap-3">
            <span
              className={cn(
                'flex size-5 items-center justify-center rounded-full',
                dotBg,
              )}
            >
              {progress === 'prev' && <CheckIcon className="size-4" />}
            </span>
            <div>{children}</div>
          </div>

          {/* connecting line */}
          {showLine && (
            <svg
              width="20"
              height="64"
              viewBox="0 0 20 64"
              className="-my-[10px]"
            >
              {/* gradient only for the segment that leads into the CURRENT dot */}
              {progress === 'prev' && (
                <defs>
                  <linearGradient
                    id="step-vert-gradient"
                    x1="0%"
                    y1="0%"
                    x2="0%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#FFFFFF" />
                    <stop offset="20%" stopColor="#FFFFFF" />
                    <stop offset="60%" stopColor="rgb(45,47,73)" />
                    <stop offset="100%" stopColor="rgb(45,47,73)" />
                  </linearGradient>
                </defs>
              )}
              <path
                d="M0.431249 0.892578C1.4555 5.25555 5.29755 8.49973 9.88447 8.49984C14.4555 8.49984 18.288 5.27819 19.3281 0.937986C18.1883 6.68345 13.4093 11.8024 12.4618 17.6664C10.7508 28.2556 10.7115 36.6318 12.371 46.8164C13.3477 52.8111 18.3616 57.9988 19.3887 63.8926C18.4457 59.4165 14.5498 56.0612 9.88447 56.0612C5.23222 56.0613 1.34558 59.3978 0.388672 63.8549C1.41806 57.9827 6.33785 52.7688 7.22947 46.7903C8.71151 36.851 8.71143 28.4535 7.13107 17.6964C6.26767 11.8195 1.5402 6.65221 0.431249 0.892578Z"
                fill={
                  progress === 'current'
                    ? 'rgb(45,47,73)'
                    : progress === 'prev'
                      ? index === currentStep - 1
                        ? 'url(#step-vert-gradient)'
                        : '#FFFFFF'
                      : 'rgb(45,47,73)'
                }
              />
            </svg>
          )}
        </div>
      </li>
    );
  }

  /* ---------------------------------------------------------------------- */
  /* HORIZONTAL RENDER                                                      */
  /* ---------------------------------------------------------------------- */

  /* line colour (replicates vertical rules incl. gradient) */
  const lineClass =
    progress === 'current' || progress === 'next'
      ? 'bg-[rgb(45,47,73)]'
      : progress === 'prev' && index === currentStep - 1
        ? 'bg-gradient-to-r from-white from-0% to-[rgb(45,47,73)] to-100%'
        : 'bg-white';

  return (
    <li
      {...props}
      className={cn(
        stepVariants({ orientation, progress }),
        'xs:px-1 relative min-w-0 flex-1 px-0.5 sm:px-2 md:px-3 lg:px-4',
        props.className,
      )}
    >
      {/* dot */}
      <div className="relative z-10 flex justify-center">
        <span
          className={cn(
            'flex items-center justify-center rounded-full border-2 transition-all duration-200',
            'xs:size-5 size-4 sm:size-6', // responsive dot size
            progress === 'prev'
              ? 'border-white bg-white text-black'
              : progress === 'current'
                ? 'border-white bg-white/10 text-white'
                : 'border-white/30 bg-[rgb(45,47,73)] text-white/50',
          )}
        >
          {progress === 'prev' ? (
            <CheckIcon className="xs:size-3 size-2.5 sm:size-4" />
          ) : (
            <span className="sr-only">Step {index}</span>
          )}
        </span>
      </div>

      {/* label under the dot */}
      <div
        className={cn(
          'mt-1 px-1 text-center font-medium leading-tight transition-colors duration-200',
          'xs:text-xs text-[10px] sm:text-sm md:text-base', // responsive text size
          'xs:mt-1.5 sm:mt-2 md:mt-3', // responsive margin
          progress === 'prev' ? 'text-white' : 'text-white/70',
        )}
      >
        <span className="block hyphens-auto break-words">{children}</span>
      </div>

      {/* connecting line – positioned to connect dots properly */}
      {showLine && (
        <div
          className={cn(
            'absolute z-0 h-0.5 transition-colors duration-200',
            // responsive positioning based on dot size
            'xs:left-[calc(50%+10px)] xs:top-2.5 left-[calc(50%+8px)] right-[-50%] top-2',
            'sm:left-[calc(50%+12px)] sm:top-3',
            lineClass,
          )}
        />
      )}
    </li>
  );
};
